// cd-gating-test-frontend
// cd-pipeline-test
// cd-pipeline-test-frontend
import styled from '@emotion/styled';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy } from 'react';
import * as ReactDOM from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import './instrumentation';

import { SplitFFProvider, store } from '@verantos-dev/app-state-base';
import { AuthProvider } from '@verantos-dev/core-auth';
import { AppThemeProvider, GlobalStyles } from '@verantos-dev/shared-styles';
import { Messages } from '@verantos-dev/types-common';

import App from './app/app';
import BackgroundImage from './assets/images/verantos-background.png';
import { ErrorFallback } from './components/ErrorFallback';
import locale_en from './locale/en';

import '@fortawesome/fontawesome-free/css/all.min.css';
import 'dayjs/locale/en';
import './assets/fonts/roboto.css';

// TanStack Query DevTools — lazy-loaded, only in dev/preview environments
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({ default: mod.ReactQueryDevtools }))
);
const showDevtools = ['localhost', 'dev', 'preview'].includes(import.meta.env.VITE_APP_ENV ?? '');

// Mui X Pro License
LicenseInfo.setLicenseKey(
  '7ab0abc518f1ec6ce43e50d7d9280a94Tz0xMjA1NTgsRT0xNzkyMjgxNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=' // pragma: allowlist secret
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5 * 60 * 1000, retry: 1 },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
const language = navigator.language.split(/[-_]/)[0]; // language without region code

const messages: Messages = {
  en: locale_en,
};

const AppBackground = styled.div`
  background-image: url(${BackgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
  height: 100%;
  width: 100%;
`;

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <GlobalStyles />
      <AppBackground>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <IntlProvider locale={language} messages={messages[language]} defaultLocale="en">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <AuthProvider>
                  <SplitFFProvider>
                    <AppThemeProvider>
                      <BrowserRouter basename={import.meta.env.BASE_URL}>
                        <App />
                      </BrowserRouter>
                    </AppThemeProvider>
                  </SplitFFProvider>
                </AuthProvider>
              </LocalizationProvider>
            </IntlProvider>
            {showDevtools && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
              </Suspense>
            )}
          </QueryClientProvider>
        </Provider>
      </AppBackground>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
